/** «Мессенджер → Аккаунты» — ровно то, что отдаёт `GET /api/messenger/accounts`. */
import { apiFetch, query } from "@/lib/api";

export type User = { id: number; name: string };
export type Actions = { send: number; new: number; archive: number; other: number };
export type Messages = { in: number; out: number; sender: number; other: number };
export type Account = {
  id: number;
  type_id: number;
  name: string;
  short: string | null;
  group: string | null;
  for_user: User | null;
  actions: Actions;
  messages: Messages;
  last_at: string | null;
};
export type System = {
  type_id: number;
  name: string;
  actions: Actions;
  messages: Messages;
  accounts: number;
  accounts_active: number;
  last_at: string | null;
  rhythm_days: number;
  regular: boolean;
  silent: boolean;
};
export type Column = "wa" | "tg" | "max" | "sms";
export type Phone = {
  id: number;
  phone: string;
  name: string | null;
  group: string | null;
  status: { name: string; color: string | null } | null;
  active: boolean;
  work_user: User | null;
  users: User[];
  accounts: Record<Column, Account[]>;
  busy: boolean;
};
export type MessengerAccounts = {
  date: string;
  since: string;
  totals: { actions: number; messages: number };
  systems: System[];
  phones: Phone[];
  accounts: Account[];
  unbound: { source: string | null; actions: Actions }[];
};

export function loadMessengerAccounts(day: string, all: boolean): Promise<MessengerAccounts> {
  return apiFetch<MessengerAccounts>(`/api/messenger/accounts${query({ date: day, all: all ? "1" : "0" })}`);
}

export const sum = (x: Record<string, number>) => Object.values(x).reduce((a, b) => a + b, 0);

/** «2026-07-27T21:52:00» → «27.07 21:52». */
export const dt = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`;

export type Health = {
  source: string;
  records: Actions;
  total: number;
  sends: number;
  dups: number;
  filled: { account: number; recipient: number; chat: number; msg: number };
  zero: string[];
  packets: { ok: number; error: number; wait: number };
  last_at: string | null;
  rhythm_days: number;
  regular: boolean;
  silent: boolean;
};
export type ActionUser = {
  id: number | null;
  name: string;
  sources: Record<string, { sends: number; new: number; archive: number; other: number }>;
  sends: number;
  total: number;
};
export type Category = "send" | "new" | "archive" | "other";
export type FeedItem = {
  id: number;
  at: string;
  user_id: number | null;
  source: string;
  category: Category;
  account: string | null;
  to: string | null;
  /** Телефон получателя Telegram / MAX из `sPhone`. */
  phone: string | null;
  chat_id: number | null;
  msg_id: number | null;
  text: string | null;
  dup: boolean;
};
export type MessengerActions = {
  date: string;
  totals: { records: number; sends: number; dups: number };
  health: Health[];
  users: ActionUser[];
  feed: FeedItem[];
};
export type ActionRaw = { id: number; archived: boolean; data: unknown };

export function loadMessengerActions(day: string): Promise<MessengerActions> {
  return apiFetch<MessengerActions>(`/api/messenger/actions${query({ date: day })}`);
}

export function loadActionRaw(id: number): Promise<ActionRaw> {
  return apiFetch<ActionRaw>(`/api/messenger/actions/${id}/raw`);
}

/** Подпись ритма: «регулярно» или «редко 3/30». */
export const rhythm = (x: { regular: boolean; rhythm_days: number }) =>
  x.regular ? "регулярно" : `редко ${x.rhythm_days}/30`;

export type Dir = "in" | "out" | "sender" | "other";
export type ChatRow = {
  id: number;
  ids: number[];
  system: string | null;
  type_id: number | null;
  accounts: string[];
  messenger_id: string | null;
  who: string | null;
  status_id: number | null;
  status: string | null;
  counts: Record<Dir, number>;
  users: User[];
  last: { at: string; dir: Dir; text: string | null };
  deal: { id: number; stage: string | null } | null;
};
export type MessengerChats = { date: string; clients: ChatRow[]; groups: ChatRow[] };
export type ChatItem = {
  kind: "msg" | "media";
  id: number;
  at: string;
  dir: Dir | null;
  type: string | null;
  author: string | null;
  text: string | null;
  template: string | null;
  url: string | null;
  image: boolean;
};
export type ChatCard = {
  chat: {
    id: number;
    ids: number[];
    group: boolean;
    system: string | null;
    accounts: string[];
    messenger_id: string | null;
    who: string | null;
    status: string | null;
    first_at: string | null;
  };
  phones: string[];
  deals: { id: number; stage: string | null; at: string | null }[];
  binds: { lead_id: number | null; deal_id: number | null; source: string | null; at: string | null }[];
  items: ChatItem[];
  more: boolean;
  before: string | null;
};

export function loadMessengerChats(day: string): Promise<MessengerChats> {
  return apiFetch<MessengerChats>(`/api/messenger/chats${query({ date: day })}`);
}

export function loadChat(id: number, before?: string | null): Promise<ChatCard> {
  return apiFetch<ChatCard>(`/api/messenger/chats/${id}${before ? query({ before }) : ""}`);
}

export const DIR_LABEL: Record<Dir, string> = { in: "вх", out: "исх", sender: "рассылка", other: "другое" };
